import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarClock, MapPin, Pencil, PowerOff, Repeat, Clock, Zap, Trash2, RotateCcw } from "lucide-react";
import AppointmentDayActionDialog from "@/components/AppointmentDayActionDialog";

const DOW_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// Week starts on Monday and is anchored to Naples-EDT so the calendar
// matches what the driver sees in the field, regardless of where the
// dispatcher's browser thinks it is.
const NAPLES_TZ = "America/New_York";

// Today's Naples calendar day (YYYY-MM-DD). Never touches the browser tz.
function naplesTodayYMD() {
  return new Date().toLocaleDateString("en-CA", { timeZone: NAPLES_TZ });
}

// Parse "YYYY-MM-DD" into an object with numeric fields.
function parseYMD(s) {
  const [y, m, d] = s.split("-").map(Number);
  return { y, m, d };
}

// Format YMD numeric parts back to string.
function formatYMD({ y, m, d }) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${y}-${pad(m)}-${pad(d)}`;
}

// Add `n` days to a YMD string using UTC math so DST never shifts us.
function addDaysYMD(ymd, n) {
  const { y, m, d } = parseYMD(ymd);
  const t = Date.UTC(y, m - 1, d) + n * 86400_000;
  const dt = new Date(t);
  return formatYMD({ y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() });
}

// Weekday index (Mon=0 .. Sun=6) for a YMD, using pure UTC math so the answer
// doesn't depend on the browser's timezone.
function weekdayOfYMD(ymd) {
  const { y, m, d } = parseYMD(ymd);
  const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // Sun=0..Sat=6
  return (js + 6) % 7; // Mon=0..Sun=6
}

function mondayOfWeek(ymd) {
  return addDaysYMD(ymd, -weekdayOfYMD(ymd));
}

/**
 * @param {{appointments: Array, onEdit: Function, onToggle: Function}} props
 */
export default function AppointmentsWeekView({ appointments = [], onEdit, onToggle, onDropNow, onDelete, onSkipDay, onUnskipDay }) {
  const todayYMD = naplesTodayYMD();
  const [anchorYMD, setAnchorYMD] = useState(() => mondayOfWeek(todayYMD));
  const [dayAction, setDayAction] = useState(null); // { appointment, ymd }
  const mondayYMD = anchorYMD;

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const ymd = addDaysYMD(mondayYMD, i);
      const { y, m, d } = parseYMD(ymd);
      // A synthetic Date built at UTC noon just so components that want a
      // `Date` object (like the DOM `.getDate()` call in the header) still
      // work — but we never trust its local-tz values for scheduling.
      const date = new Date(Date.UTC(y, m - 1, d, 12));
      return { date, ymd, weekday: i };
    });
  }, [mondayYMD]);

  // Bucket appointments per calendar day for this week.
  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[d.ymd] = []));
    for (const a of appointments) {
      const hasWeekdays = (a.weekdays || []).length > 0;
      const startYMD = a.start_date || "";
      const endYMD = a.end_date || "";
      // A date range (start + end) WITHOUT specific weekdays means "every
      // day between those two dates" — matches the user's mental model of
      // "escribo desde y hasta = se repite en esos días exactos". A single
      // start_date with no end is still a one-off booking on that day.
      const hasRange = Boolean(startYMD && endYMD);
      for (const d of days) {
        if (startYMD && d.ymd < startYMD) continue;
        if (endYMD && d.ymd > endYMD) continue;
        if (hasWeekdays) {
          if (a.weekdays.includes(d.weekday)) map[d.ymd].push(a);
        } else if (hasRange) {
          map[d.ymd].push(a);
        } else if (d.ymd === startYMD) {
          map[d.ymd].push(a);
        }
      }
    }
    Object.values(map).forEach((list) => list.sort((x, y) => (x.hour - y.hour) || (x.minute - y.minute)));
    return map;
  }, [appointments, days]);

  const isSkipped = (a, ymd) => (a.skipped_days || []).includes(ymd);
  const totalThisWeek = Object.entries(byDay).reduce((s, [ymd, l]) => s + l.filter((a) => !isSkipped(a, ymd)).length, 0);
  const maxPerDay = Math.max(1, ...Object.values(byDay).map((l) => l.length));

  const shiftWeek = (delta) => setAnchorYMD((cur) => addDaysYMD(cur, delta * 7));
  const goToday = () => setAnchorYMD(mondayOfWeek(todayYMD));

  const rangeLabel = (() => {
    const end = addDaysYMD(mondayYMD, 6);
    const fmt = (ymd) => {
      const { y, m, d } = parseYMD(ymd);
      return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
    };
    return `${fmt(mondayYMD)} – ${fmt(end)}`;
  })();

  return (
    <div className="space-y-3">
      {/* Week nav */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="week-prev" className="border-border h-8 w-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-mono text-sm text-white/90 tracking-wide" data-testid="week-range">
            {rangeLabel}
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="week-next" className="border-border h-8 w-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="week-today" className="border-primary/40 text-primary hover:bg-primary/10 h-8">
            Hoy
          </Button>
        </div>
        <div className="text-xs font-mono text-muted-foreground" data-testid="week-total">
          {totalThisWeek} cita{totalThisWeek !== 1 ? "s" : ""} programada{totalThisWeek !== 1 ? "s" : ""} esta semana
        </div>
      </div>

      {/* Grid: 7 columns, one per day. Below md the grid collapses to 1 col. */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d) => {
          const list = byDay[d.ymd];
          const load = list.length / maxPerDay;
          const isToday = d.ymd === todayYMD;
          // Heat color: transparent → primary/25 as load grows.
          const heatBg = list.length === 0
            ? "bg-card"
            : load >= 0.75 ? "bg-amber-500/15 border-amber-500/40"
            : load >= 0.4 ? "bg-primary/10 border-primary/40"
            : "bg-sky-500/5 border-sky-500/30";
          return (
            <Card
              key={d.ymd}
              data-testid={`week-day-${d.weekday}`}
              className={`p-2 border transition-all ${isToday ? "bg-primary/20 border-primary shadow-[0_0_18px_rgba(251,191,36,0.35)] ring-2 ring-primary" : heatBg}`}
            >
              <div className="flex items-baseline justify-between mb-1.5 px-0.5">
                <div>
                  <div className={`text-[10px] font-mono uppercase tracking-widest ${isToday ? "text-primary font-black" : "text-muted-foreground"}`}>
                    {isToday ? "★ HOY" : DOW_LABELS[d.weekday]}
                  </div>
                  <div className={`text-lg font-black leading-none ${isToday ? "text-primary" : "text-white/90"}`}>
                    {parseYMD(d.ymd).d}
                  </div>
                </div>
                <div
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${list.length === 0 ? "text-muted-foreground/50" : "bg-black/30 text-white"}`}
                  data-testid={`week-day-count-${d.weekday}`}
                >
                  {list.filter((a) => !isSkipped(a, d.ymd)).length}
                </div>
              </div>

              <div className="space-y-1">
                {list.length === 0 && (
                  <div className="text-[10px] text-muted-foreground/40 text-center py-3">—</div>
                )}
                {list.map((a) => isSkipped(a, d.ymd) ? (
                  <div
                    key={`${d.ymd}-${a.id}`}
                    data-testid={`week-appt-skipped-${a.id}-${d.weekday}`}
                    className="flex items-center justify-between gap-1 rounded border border-dashed border-border/60 px-1.5 py-1 text-[10px] text-muted-foreground/70"
                    title="Quitada solo este día"
                  >
                    <span className="truncate line-through">
                      {String(a.hour).padStart(2, "0")}:{String(a.minute).padStart(2, "0")} {a.passenger_name || "—"}
                    </span>
                    {onUnskipDay && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onUnskipDay(a, d.ymd); }}
                        className="h-4 w-4 grid place-items-center rounded hover:bg-emerald-500/20 text-emerald-400 shrink-0"
                        data-testid={`week-unskip-${a.id}-${d.weekday}`}
                        title="Volver a poner este día"
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    key={`${d.ymd}-${a.id}`}
                    data-testid={`week-appt-${a.id}-${d.weekday}`}
                    className={`group relative rounded border px-1.5 py-1 text-[11px] leading-tight cursor-pointer transition-all ${
                      a.active ? "bg-card/70 border-border hover:border-primary" : "bg-card/40 border-border/50 opacity-60"
                    }`}
                    onClick={() => onEdit?.(a)}
                    title={`${a.passenger_name || "sin nombre"} · ${a.pickup_address || ""} → ${a.dropoff_address || ""}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono font-bold text-primary">
                        {String(a.hour).padStart(2, "0")}:{String(a.minute).padStart(2, "0")}
                      </span>
                      {a.weekdays?.length > 0 ? (
                        <Repeat className="w-2.5 h-2.5 text-emerald-400" />
                      ) : (
                        <Clock className="w-2.5 h-2.5 text-sky-400" />
                      )}
                    </div>
                    <div className="truncate font-semibold text-white/90">
                      {a.passenger_name || <span className="text-muted-foreground">—</span>}
                    </div>
                    <div className="truncate text-muted-foreground text-[10px] flex items-center gap-0.5">
                      <MapPin className="w-2.5 h-2.5 text-primary shrink-0" />
                      {a.pickup_address || "—"}
                    </div>
                    {/* Hover actions */}
                    <div className="absolute right-1 top-1 hidden group-hover:flex items-center gap-0.5">
                      {onDropNow && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDropNow(a); }}
                          className="h-5 w-5 grid place-items-center rounded bg-amber-500/70 hover:bg-amber-500 text-black"
                          data-testid={`week-dropnow-${a.id}-${d.weekday}`}
                          title="Bajar ahora al panel"
                        >
                          <Zap className="w-2.5 h-2.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggle?.(a); }}
                        className="h-5 w-5 grid place-items-center rounded bg-black/50 hover:bg-black/70 text-white/80"
                        data-testid={`week-toggle-${a.id}-${d.weekday}`}
                        title={a.active ? "Pausar" : "Reactivar"}
                      >
                        <PowerOff className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEdit?.(a); }}
                        className="h-5 w-5 grid place-items-center rounded bg-black/50 hover:bg-black/70 text-white/80"
                        data-testid={`week-edit-${a.id}-${d.weekday}`}
                        title="Editar"
                      >
                        <Pencil className="w-2.5 h-2.5" />
                      </button>
                      {(onDelete || onSkipDay) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDayAction({ appointment: a, ymd: d.ymd }); }}
                          className="h-5 w-5 grid place-items-center rounded bg-rose-600/70 hover:bg-rose-600 text-white"
                          data-testid={`week-delete-${a.id}-${d.weekday}`}
                          title="Quitar este día / eliminar"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <AppointmentDayActionDialog
        target={dayAction}
        onClose={() => setDayAction(null)}
        onSkipDay={onSkipDay}
        onDelete={onDelete}
      />

      {totalThisWeek === 0 && (
        <div className="text-center text-muted-foreground text-xs py-2 flex items-center justify-center gap-2">
          <CalendarClock className="w-3.5 h-3.5" /> Sin citas esta semana.
        </div>
      )}
    </div>
  );
}
